import { useEffect, useCallback } from 'react';
import { getVinStylesDataWithMetadata } from 'client/data/models/vehicle-vin';
import { getMetricApiOptions } from 'client/utils/metrics-hoc';
import {
  fireAppraisalHowManyVinsPixel,
  fireAppraisalPixel,
  fireLicensePlateServicePixel,
  fireDecodePixels,
  fireVinErrorTracking,
} from 'site-modules/shared/utils/appraisal/common-utils';
import { useAppraisalTabsState } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

export function useLicensePlateLookup({
  vinFromLicensePlate,
  creativeId,
  apiMetrics,
  licensePlate,
  isLpAndVinFormComponent,
  setStylesAndVin,
}) {
  const { setColors, setStyleIds, setLicensePlate, setStateCode } = useAppraisalTabsState();

  const prevVinFromLicensePlate = usePrevious(vinFromLicensePlate);

  const vinValueFromLp = vinFromLicensePlate?.vin;
  const vinLength = vinFromLicensePlate?.vins?.length;

  // Fire how-many-VINs pixel when vinFromLicensePlate changes
  useEffect(() => {
    if (vinFromLicensePlate && vinFromLicensePlate !== prevVinFromLicensePlate) {
      fireAppraisalHowManyVinsPixel('license_plate_lookup_vins_found', vinLength, creativeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinFromLicensePlate]);

  // Fire service/VIN pixels when vinValueFromLp changes
  useEffect(() => {
    if (typeof vinValueFromLp !== 'string') return;
    if (vinFromLicensePlate === prevVinFromLicensePlate) return;

    if (vinValueFromLp) {
      fireAppraisalPixel('License Plate', creativeId);
      if (vinFromLicensePlate.service === 'experian') {
        fireLicensePlateServicePixel('vin_found_experian', vinValueFromLp, creativeId);
      } else {
        fireLicensePlateServicePixel('vin_found_ihs', vinValueFromLp, creativeId);
        fireLicensePlateServicePixel('vin_not_found_experian', null, creativeId);
      }
    } else {
      fireLicensePlateServicePixel('vin_not_found_ihs', null, creativeId);
      fireLicensePlateServicePixel('vin_not_found_experian', null, creativeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinValueFromLp]);

  const lookupVin = useCallback(
    (lp, stateCode) => {
      setLicensePlate(lp);
      setStateCode(stateCode);
    },
    [setLicensePlate, setStateCode]
  );

  const decodeAndSetStyles = useCallback(
    async vinValue => {
      const vinStylesData = await getVinStylesDataWithMetadata({
        vin: vinValue,
        apiOptions: getMetricApiOptions(apiMetrics),
      });
      const { results: styles = [], exteriorColor, interiorColor } = vinStylesData;

      if (!isLpAndVinFormComponent) {
        fireDecodePixels({
          stylesLength: styles?.length,
          vinStylesData,
          creativeId,
        });
        if (vinStylesData?.isPubStateInvalid) {
          fireVinErrorTracking(vinValue, vinStylesData, creativeId);
        }
      }

      setColors({ exteriorColor, interiorColor });
      setStylesAndVin(styles, vinValue, licensePlate);
      const styleIds = styles.map(({ styleId }) => styleId);
      setStyleIds(styleIds);

      return vinStylesData;
    },
    [apiMetrics, creativeId, isLpAndVinFormComponent, licensePlate, setColors, setStyleIds, setStylesAndVin]
  );

  return { lookupVin, decodeAndSetStyles };
}
